import type { FundingApplicationSchemas } from "api-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useCustomerAppConfig } from "undercarriage";

import { getFundingApplicationStagesQueryOptions } from "../../../queries/funding-application-configuration/queries";

/**
 * Determines whether to show the opt-out checkbox for a document stage.
 *
 * @param documentType - The type of document to check
 * @returns An object with:
 *   - `useLegacyCheckbox`: If `true`, use old business logic and ignore `showOptOutCheckbox`
 *   - `canOptOutOfStage`: If `useLegacyCheckbox` is `false`, indicates whether the stage allows opt-out
 *
 * TODO: Deprecate `useLegacyCheckbox` when all feature flag removed
 */
export const useStageOptOutCheckbox = (
  documentType: FundingApplicationSchemas.DocumentType
): {
  useLegacyCheckbox: boolean;
  canOptOutOfStage?: boolean;
} => {
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const fundingApplicationConfigurationEnabled = useFeatureFlagEnabled("enable_funding_application_config");
  const { data: fundingApplicationStages } = useSuspenseQuery(getFundingApplicationStagesQueryOptions(customerDomain));

  if (fundingApplicationConfigurationEnabled && fundingApplicationStages) {
    if (documentType === "profit-and-loss" || documentType === "balance-sheet") {
      return {
        useLegacyCheckbox: false,
        canOptOutOfStage: !!fundingApplicationStages.accounting?.canOptOut,
      };
    }
    const stage = fundingApplicationStages?.documents?.find((stage) => stage.documentType === documentType);
    return {
      useLegacyCheckbox: false,
      canOptOutOfStage: !!stage?.canOptOut,
    };
  }
  return {
    useLegacyCheckbox: true,
  };
};
